/**
 * Sprint 3.7 Etapa 3 — Página de auditoria de rollouts com waves + ações.
 *
 * Acréscimos sobre Etapa 2:
 *   - Coluna "Waves" com barras visuais por wave (5%/20%/50%/100%)
 *   - Linha expandida mostra detalhes de cada wave + tabela de comandos
 *   - Ações por batch progressivo: PAUSE / RESUME / CANCEL / ROLLBACK
 *   - Botão "+ Rollout progressivo" no header (cria batch com waves)
 *
 * Decisões:
 *   - Barras: caracteres unicode █░ (sem deps de chart libs)
 *   - Ações usam window.confirm() para reduzir cliques acidentais
 *   - ROLLBACK pede release_id via prompt (Sprint 3.8 pode evoluir para
 *     dropdown carregado de listFirmwareReleases)
 *   - Refresh manual (consistente com Etapa 2)
 */
import { useCallback, useEffect, useState } from "react";

import {
  cancelRolloutBatch,
  getRolloutBatchCommands,
  listFirmwareReleases,
  listRolloutBatches,
  pauseRolloutBatch,
  resumeRolloutBatch,
  rollbackRolloutBatch,
  triggerRolloutProgressive,
} from "../lib/api.js";
import { useCriticalConfirm } from "../components/ConfirmCriticalAction.jsx";
import { t } from "../i18n/index.js";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatTtl(seconds) {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

/**
 * Renderiza barra de progresso unicode █░ proporcional.
 * Ex: progress(40, 10) → "████░░░░░░"
 */
function progressBar(percentDone, width = 10) {
  const filled = Math.max(0, Math.min(width, Math.round((percentDone / 100) * width)));
  return "█".repeat(filled) + "░".repeat(width - filled);
}

function batchStatusClass(status) {
  switch (status) {
    case "completed_success":
      return "text-noc-ok";
    case "in_progress":
      return "text-noc-warn";
    case "completed_partial":
      return "text-noc-orange";
    case "expired":
      return "text-noc-err";
    case "empty":
      return "text-noc-muted";
    default:
      return "text-noc-text";
  }
}

function lifecycleClass(lifecycle) {
  switch (lifecycle) {
    case "active":
      return "text-noc-ok";
    case "paused":
      return "text-noc-warn";
    case "completed":
      return "text-noc-ok";
    case "cancelled":
      return "text-noc-err";
    case "rolled_back":
      return "text-noc-orange";
    default:
      return "text-noc-muted";
  }
}

function waveStatusClass(status) {
  switch (status) {
    case "running":
      return "text-noc-warn";
    case "finished":
      return "text-noc-ok";
    case "paused":
      return "text-noc-warn";
    case "cancelled":
      return "text-noc-err";
    case "planned":
      return "text-noc-muted";
    default:
      return "text-noc-text";
  }
}

function commandStatusClass(status) {
  switch (status) {
    case "acked":
      return "text-noc-ok";
    case "pending":
    case "delivered":
      return "text-noc-warn";
    case "failed":
      return "text-noc-err";
    case "expired":
      return "text-noc-muted";
    default:
      return "text-noc-text";
  }
}

export default function Rollouts() {
  // S3.10C2 — confirmação forte para OTA/rollout (hook no escopo de uso).
  const confirmCritical = useCriticalConfirm();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [releases, setReleases] = useState([]);
  const [filterReleaseId, setFilterReleaseId] = useState("");
  const [limit, setLimit] = useState(50);

  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [expandedCommands, setExpandedCommands] = useState([]);
  const [loadingCommands, setLoadingCommands] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    listFirmwareReleases({ limit: 200, signal: ctl.signal })
      .then((d) => setReleases(d?.releases || []))
      .catch(() => {});
    return () => ctl.abort();
  }, []);

  const refresh = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        setError(null);
        const opts = { limit, signal };
        if (filterReleaseId) opts.release_id = filterReleaseId;
        const data = await listRolloutBatches(opts);
        setBatches(data?.batches || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Erro ao carregar rollouts");
      } finally {
        setLoading(false);
      }
    },
    [filterReleaseId, limit]
  );

  useEffect(() => {
    const ctl = new AbortController();
    refresh(ctl.signal);
    return () => ctl.abort();
  }, [refresh]);

  const handleToggleExpand = async (batchId) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      setExpandedCommands([]);
      return;
    }
    setExpandedBatchId(batchId);
    setExpandedCommands([]);
    try {
      setLoadingCommands(true);
      const data = await getRolloutBatchCommands(batchId);
      setExpandedCommands(data?.commands || []);
    } catch (e) {
      setError(e?.message || "Erro ao carregar comandos do batch");
    } finally {
      setLoadingCommands(false);
    }
  };

  // ===== Ações =====

  const handleTriggerProgressive = async () => {
    if (releases.length === 0) {
      window.alert("Nenhum release disponível.");
      return;
    }
    const releaseList = releases
      .slice(0, 20)
      .map((r, i) => `${i + 1}. ${r.version}`)
      .join("\n");
    const idx = window.prompt(
      `Escolha um release (1..${Math.min(releases.length, 20)}):\n\n${releaseList}`,
      "1"
    );
    if (idx === null) return;
    const n = parseInt(idx, 10);
    if (!Number.isFinite(n) || n < 1 || n > releases.length) {
      window.alert("Escolha inválida");
      return;
    }
    const release = releases[n - 1];

    const targetBefore = window.prompt(
      `Rollout progressivo de ${release.version}.\n\nfw_version atual dos devices (target_version_before).\nDeixe vazio para devices SEM fw_version.\nEx: 2.4.4`,
      "2.4.4"
    );
    if (targetBefore === null) return;

    const wavesStr = window.prompt(
      "Waves (percentuais cumulativos crescentes, terminando em 100):",
      "5,20,50,100"
    );
    if (wavesStr === null) return;
    const waves = wavesStr.split(",").map((s) => parseInt(s.trim(), 10));
    if (waves.some((p) => !Number.isFinite(p) || p < 1 || p > 100)) {
      window.alert("Waves inválido");
      return;
    }

    const _secret = await confirmCritical({
      actionLabel: "Criar rollout progressivo (OTA em massa)",
      target: {},
      impact: "Vai atualizar firmware progressivamente em ondas (canário → 100%).",
    });
    if (_secret == null) return;
    try {
      const r = await triggerRolloutProgressive(release.id, {
        target_version_before: targetBefore,
        waves,
      }, { confirm: _secret });
      window.alert(
        `Rollout progressivo criado.\n\n` +
          `batch_id: ${r.batch_id}\n` +
          `candidates: ${r.candidates_total}\n` +
          `wave 1: ${r.waves[0]?.enqueued_devices || 0} devices enfileirados`
      );
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao criar rollout progressivo");
    }
  };

  const handlePause = async (batchId) => {
    if (!window.confirm(`Pausar batch ${batchId.slice(0, 8)}…?`)) return;
    try {
      const r = await pauseRolloutBatch(batchId);
      window.alert(`Pausado. ${r.message || ""}`);
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao pausar");
    }
  };

  const handleResume = async (batchId) => {
    if (!window.confirm(`Retomar batch ${batchId.slice(0, 8)}…?`)) return;
    try {
      const r = await resumeRolloutBatch(batchId);
      window.alert(`Resumido. ${r.message || ""}`);
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao retomar");
    }
  };

  const handleCancel = async (batchId) => {
    if (
      !window.confirm(
        `Cancelar batch ${batchId.slice(0, 8)}…?\n\n` +
          "Todos os comandos pending/delivered serão expirados."
      )
    )
      return;
    try {
      const r = await cancelRolloutBatch(batchId);
      window.alert(`Cancelado. ${r.message || ""}`);
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao cancelar");
    }
  };

  const handleRollback = async (batch) => {
    if (releases.length === 0) {
      window.alert("Nenhum release disponível para rollback.");
      return;
    }
    const releaseList = releases
      .filter((r) => r.id !== batch.release_id)
      .slice(0, 20)
      .map((r, i) => `${i + 1}. ${r.version}`)
      .join("\n");
    const idx = window.prompt(
      `Rollback de ${batch.release_version || "?"}.\n\n` +
        `Escolha o release ANTERIOR (devices atualmente em ${batch.release_version || "?"} voltarão para ele):\n\n` +
        releaseList,
      "1"
    );
    if (idx === null) return;
    const n = parseInt(idx, 10);
    const filtered = releases.filter((r) => r.id !== batch.release_id);
    if (!Number.isFinite(n) || n < 1 || n > filtered.length) {
      window.alert("Escolha inválida");
      return;
    }
    const prev = filtered[n - 1];
    const _secret = await confirmCritical({
      actionLabel: "Rollback de rollout (OTA de volta)",
      target: {},
      impact: `Devices em ${batch.release_version || "?"} receberão OTA de volta para ${prev.version}.`,
    });
    if (_secret == null) return;
    try {
      const r = await rollbackRolloutBatch(batch.id, {
        previous_release_id: prev.id,
      }, { confirm: _secret });
      window.alert(
        `Rollback criado.\n\n` +
          `Novo batch: ${r.batch_id}\n` +
          `${r.affected_commands} devices enfileirados.`
      );
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao criar rollback");
    }
  };

  return (
    <div className="space-y-4 max-w-7xl mx-auto p-4">
      <header className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight mb-1">{t("dashboard.rolloutsAudit")}</h1>
          <p className="text-sm text-noc-muted">
            Sprint 3.7 — histórico de lotes. Etapa 3: rollout progressivo com
            waves (canary), pause/resume/cancel/rollback.
          </p>
        </div>
        <button
          type="button"
          onClick={handleTriggerProgressive}
          className="text-xs px-3 py-1 rounded border border-noc-ok/30 text-noc-ok hover:border-noc-ok/30"
        >
          + Rollout progressivo
        </button>
      </header>

      {/* Filtros */}
      <section className="card-pad space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">
            Release:
          </label>
          <select
            value={filterReleaseId}
            onChange={(e) => setFilterReleaseId(e.target.value)}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-sm font-mono min-w-[200px]"
          >
            <option value="">Todos</option>
            {releases.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version} ({r.sha256.slice(0, 8)}…)
              </option>
            ))}
          </select>

          <label className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">
            Limit:
          </label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-sm"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>

          <button
            type="button"
            onClick={() => refresh()}
            disabled={loading}
            className="ml-auto text-xs px-3 py-1 rounded border border-noc-muted/40 hover:border-noc-text/60 disabled:opacity-50"
          >
            {loading ? t("common.loading") : `🔄 ${t("common.refresh")}`}
          </button>
        </div>
      </section>

      {error && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 break-words">
          {error}
        </div>
      )}

      {/* Tabela de batches */}
      <section className="card-pad">
        {loading && batches.length === 0 ? (
          <div className="text-sm text-noc-muted">Carregando...</div>
        ) : batches.length === 0 ? (
          <div className="text-sm text-noc-muted">
            Nenhum rollout no histórico com os filtros atuais.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint border-b border-noc-border">
                  <th className="py-2 pr-3 w-6"></th>
                  <th className="py-2 pr-3">Batch</th>
                  <th className="py-2 pr-3">Release</th>
                  <th className="py-2 pr-3">From → To</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Waves</th>
                  <th className="py-2 pr-3 text-right">Enq</th>
                  <th className="py-2 pr-3">Counts (P/D/A/F/E)</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Lifecycle</th>
                  <th className="py-2 pr-3">Actions</th>
                  <th className="py-2 pr-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <BatchRow
                    key={b.id}
                    batch={b}
                    expanded={expandedBatchId === b.id}
                    onToggle={() => handleToggleExpand(b.id)}
                    commands={expandedBatchId === b.id ? expandedCommands : []}
                    loadingCommands={
                      expandedBatchId === b.id && loadingCommands
                    }
                    onPause={() => handlePause(b.id)}
                    onResume={() => handleResume(b.id)}
                    onCancel={() => handleCancel(b.id)}
                    onRollback={() => handleRollback(b)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function BatchRow({
  batch,
  expanded,
  onToggle,
  commands,
  loadingCommands,
  onPause,
  onResume,
  onCancel,
  onRollback,
}) {
  const c = batch.counts || {};
  const isProg = batch.is_progressive;
  const lifecycle = batch.lifecycle_status;
  const canPause = isProg && lifecycle === "active";
  const canResume = isProg && lifecycle === "paused";
  const canCancel =
    isProg && (lifecycle === "active" || lifecycle === "paused");
  const canRollback =
    isProg &&
    (lifecycle === "completed" ||
      lifecycle === "active" ||
      lifecycle === "paused");

  return (
    <>
      <tr
        className="border-b border-noc-border/30 hover:bg-noc-bg/40 cursor-pointer"
        onClick={onToggle}
      >
        <td className="py-2 pr-3 text-noc-muted">{expanded ? "▼" : "▶"}</td>
        <td className="py-2 pr-3 font-mono text-xs" title={batch.id}>
          {batch.id.slice(0, 8)}…
        </td>
        <td className="py-2 pr-3 font-mono">
          {batch.release_version || (
            <span className="text-noc-err italic" title="release deletado">
              deleted
            </span>
          )}
        </td>
        <td className="py-2 pr-3 text-xs">
          <span className="font-mono">
            {batch.target_version_before || (
              <span className="font-mono text-[12px] text-noc-faint">no-fw</span>
            )}
          </span>
          <span className="text-noc-muted"> → </span>
          <span className="font-mono">{batch.release_version || "?"}</span>
        </td>
        <td className="py-2 pr-3 text-xs">
          {isProg ? (
            <span className="text-noc-ok">progressive</span>
          ) : (
            <span className="text-noc-muted">simple</span>
          )}
        </td>
        <td className="py-2 pr-3">
          <WavesMini waves={batch.waves || []} />
        </td>
        <td className="py-2 pr-3 font-mono text-right text-noc-ok">
          {batch.enqueued_count}
        </td>
        <td className="py-2 pr-3 font-mono text-xs">
          <span title="pending" className="text-noc-warn">
            {c.pending || 0}
          </span>
          <span className="text-noc-muted">/</span>
          <span title="delivered" className="text-noc-warn">
            {c.delivered || 0}
          </span>
          <span className="text-noc-muted">/</span>
          <span title="acked" className="text-noc-ok">
            {c.acked || 0}
          </span>
          <span className="text-noc-muted">/</span>
          <span title="failed" className="text-noc-err">
            {c.failed || 0}
          </span>
          <span className="text-noc-muted">/</span>
          <span title="expired" className="text-noc-muted">
            {c.expired || 0}
          </span>
        </td>
        <td className="py-2 pr-3">
          <span
            className={`${batchStatusClass(batch.status)} font-mono text-xs`}
          >
            {(batch.status || "?").toUpperCase().replace(/_/g, " ")}
          </span>
        </td>
        <td className="py-2 pr-3 font-mono text-xs">
          {lifecycle ? (
            <span className={lifecycleClass(lifecycle)}>{lifecycle}</span>
          ) : (
            <span className="text-noc-muted">—</span>
          )}
        </td>
        <td
          className="py-2 pr-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-1">
            {canPause && (
              <button
                onClick={onPause}
                title="Pause"
                className="text-xs px-1.5 py-0.5 rounded border border-noc-warn/30 text-noc-warn hover:border-noc-warn/30"
              >
                ⏸
              </button>
            )}
            {canResume && (
              <button
                onClick={onResume}
                title="Resume"
                className="text-xs px-1.5 py-0.5 rounded border border-noc-ok/30 text-noc-ok hover:border-noc-ok/30"
              >
                ▶
              </button>
            )}
            {canCancel && (
              <button
                onClick={onCancel}
                title={t("common.cancel")}
                className="text-xs px-1.5 py-0.5 rounded border border-noc-err/30 text-noc-err hover:border-noc-err/30"
              >
                ✕
              </button>
            )}
            {canRollback && (
              <button
                onClick={onRollback}
                title="Rollback"
                className="text-xs px-1.5 py-0.5 rounded border border-noc-orange/30 text-noc-orange hover:border-noc-orange/30"
              >
                ↺
              </button>
            )}
          </div>
        </td>
        <td className="py-2 pr-3 text-xs text-noc-muted">
          {formatDate(batch.created_at)}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-noc-bg/20">
          <td
            colSpan={12}
            className="py-3 px-4 border-b border-noc-border/30"
          >
            {(batch.waves || []).length > 0 && (
              <WavesDetail waves={batch.waves} />
            )}
            <BatchCommandsTable
              commands={commands}
              loading={loadingCommands}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Mini-visualização das waves do batch (na coluna Waves da linha).
 * Mostra cada wave como barra unicode pequena com seu percent.
 */
function WavesMini({ waves }) {
  if (waves.length === 0) {
    return <span className="text-noc-muted">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 font-mono text-xs">
      {waves.map((w) => {
        const done =
          w.planned_devices > 0
            ? (w.enqueued_devices / w.planned_devices) * 100
            : 0;
        return (
          <div key={w.id} className="flex items-center gap-1.5">
            <span className="w-7 text-right">{w.target_percent}%</span>
            <span className={waveStatusClass(w.status)}>
              {progressBar(w.status === "finished" ? 100 : done, 8)}
            </span>
            <span className={`${waveStatusClass(w.status)} text-xs`}>
              {w.status[0].toUpperCase()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function WavesDetail({ waves }) {
  return (
    <div className="mb-3">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint mb-2">
        Waves
      </h3>
      <div className="space-y-1.5">
        {waves.map((w) => {
          const done =
            w.planned_devices > 0
              ? (w.enqueued_devices / w.planned_devices) * 100
              : 0;
          return (
            <div
              key={w.id}
              className="flex items-center gap-3 text-xs font-mono"
            >
              <span className="w-16">Wave {w.wave_index}</span>
              <span className="w-10 text-right">{w.target_percent}%</span>
              <span className={`${waveStatusClass(w.status)} w-32`}>
                {progressBar(w.status === "finished" ? 100 : done, 20)}
              </span>
              <span className="w-32">
                {w.enqueued_devices}/{w.planned_devices} devices
              </span>
              <span
                className={`${waveStatusClass(w.status)} uppercase tracking-wider`}
              >
                {w.status}
              </span>
              {w.started_at && (
                <span className="text-noc-muted">
                  started: {formatDate(w.started_at)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BatchCommandsTable({ commands, loading }) {
  if (loading) {
    return (
      <div className="text-xs text-noc-muted py-2">Carregando comandos...</div>
    );
  }
  if (commands.length === 0) {
    return (
      <div className="text-xs text-noc-muted py-2">
        Nenhum comando linkado a este lote.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint mb-2">
        Commands ({commands.length})
      </h3>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left uppercase tracking-wide text-noc-muted border-b border-noc-border/40">
            <th className="py-1 pr-3">Device</th>
            <th className="py-1 pr-3">Status</th>
            <th className="py-1 pr-3">Created</th>
            <th className="py-1 pr-3">Delivered</th>
            <th className="py-1 pr-3">Acked</th>
            <th className="py-1 pr-3">Last ack</th>
          </tr>
        </thead>
        <tbody>
          {commands.map((cmd) => (
            <tr
              key={cmd.command_id}
              className="border-b border-noc-border/20 hover:bg-noc-bg/40"
            >
              <td className="py-1 pr-3 font-mono">{cmd.device_id}</td>
              <td className="py-1 pr-3">
                <span className={`${commandStatusClass(cmd.status)} font-mono`}>
                  {cmd.status}
                </span>
              </td>
              <td className="py-1 pr-3 text-noc-muted">
                {formatDate(cmd.created_at)}
              </td>
              <td className="py-1 pr-3 text-noc-muted">
                {formatDate(cmd.delivered_at)}
              </td>
              <td className="py-1 pr-3 text-noc-muted">
                {formatDate(cmd.acked_at)}
              </td>
              <td className="py-1 pr-3">
                {cmd.last_ack_success === true ? (
                  <span className="text-noc-ok">success</span>
                ) : cmd.last_ack_success === false ? (
                  <span
                    className="text-noc-err cursor-help"
                    title={cmd.last_ack_message || ""}
                  >
                    failed
                    {cmd.last_ack_message && (
                      <span className="text-noc-muted">
                        {": "}
                        {cmd.last_ack_message.slice(0, 30)}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-noc-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
